import { useState } from 'react'

import { useToasts } from '@/hooks/useToasts'
import { UPLOAD_ERROR_COMPONENTS, reportUploadError } from '@/hooks/useUpload'
import { useTranslation } from 'react-i18next'

import {
  ALLOWED_MIME_TYPES,
  FILE_TYPES,
  MAX_FILENAME_LENGTH,
  MAX_FILES,
  MAX_FILE_SIZE,
} from '../constants/addNoteConstants'
import {
  validateFileName,
  validateFileSize,
  validateFileType,
} from '../utils/validation'

export const useFileSelect = () => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [previewFile, setPreviewFile] = useState<File | null>(null)
  const { showError } = useToasts()
  const { t } = useTranslation()

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || [])

    // selectedFiles is render-time state, so files accepted earlier in this
    // same picker event must be tracked separately or duplicates slip through.
    const acceptedNames = new Set(
      selectedFiles.map((selectedFile) => selectedFile.name)
    )

    // Duplicates are skipped below and never occupy a slot, so only distinct
    // genuinely-new names count against the remaining capacity.
    const newNameCount = new Set(
      files.map((file) => file.name).filter((name) => !acceptedNames.has(name))
    ).size
    const remainingCapacity = MAX_FILES - selectedFiles.length

    if (newNameCount > remainingCapacity) {
      showError(`You can only add up to ${MAX_FILES} files`)
      return
    }
    for (const file of files) {
      try {
        validateFileSize(file, MAX_FILE_SIZE)
        validateFileType(file, FILE_TYPES, ALLOWED_MIME_TYPES)
        validateFileName(
          file,
          MAX_FILENAME_LENGTH,
          t('common:filename_contains_unsupported_characters')
        )

        if (acceptedNames.has(file.name)) {
          // Skip just this file — aborting would drop valid files selected
          // after the duplicate in the same picker event.
          showError(`File "${file.name}" is already added`)
          continue
        }

        acceptedNames.add(file.name)
        setSelectedFiles((prevFiles) => [...prevFiles, file])
      } catch (error) {
        reportUploadError(error, UPLOAD_ERROR_COMPONENTS.AddNoteFileSelect)
        showError(error.message)
        return
      }
    }
  }

  const removeFile = (fileToRemove: File) => {
    const fileIndex = selectedFiles.findIndex(
      (file) => file.name === fileToRemove.name
    )
    if (fileIndex === -1) return
    selectedFiles.splice(fileIndex, 1)
    setSelectedFiles([...selectedFiles])
    if (previewFile?.name === fileToRemove.name) setPreviewFile(null)
  }

  return {
    selectedFiles,
    setSelectedFiles,
    handleFileSelect,
    removeFile,
    previewFile,
    setPreviewFile,
  }
}
